import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, Check, KeyRound, Search } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TitleCard from "../../../components/TitleCard/TitleCard";
import GradientButton from "../../../components/GradientButton/GradientButton";
import CancelButton from "../../../components/CancelButton/CancelButton";

const getResponseItems = (response) => response.data?.data || response.data || [];

const getPermissionId = (permission) => {
    if (!permission) return null;
    if (typeof permission === "string") return permission;
    return permission._id?.toString?.() || permission.id?.toString?.() || null;
};

const normalizePermission = (permission) => {
    const id = getPermissionId(permission);
    if (!id || typeof permission !== "object") return null;

    return {
        id,
        name: String(permission.name || "Permiso sin nombre"),
        code: String(permission.code || ""),
        module: String(permission.module || ""),
        description: String(permission.description || ""),
    };
};

const uniquePermissions = (items) => {
    const permissionsById = new Map();
    items.forEach((permission) => {
        if (permission && !permissionsById.has(permission.id)) {
            permissionsById.set(permission.id, permission);
        }
    });
    return Array.from(permissionsById.values());
};

const RolePermissionAssignment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { roleId } = useParams();
    const roleFromLocation = location.state?.role;

    const [role, setRole] = useState(roleFromLocation);
    const [permissions, setPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);
    const [permissionPage, setPermissionPage] = useState(0);
    const permissionsPerPage = 10;

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("access_token");
                const headers = { Authorization: `Bearer ${token}` };
                const [permissionsResponse, rolesResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/permissions/get-all`, { headers }),
                    roleFromLocation
                        ? Promise.resolve(null)
                        : axios.get(`${process.env.REACT_APP_API_URL}/roles/get-all`, { headers }),
                ]);

                const availablePermissions = getResponseItems(permissionsResponse);
                const currentRole = roleFromLocation || getResponseItems(rolesResponse)
                    .find((item) => item._id === roleId || item.id === roleId);

                setPermissions(uniquePermissions(
                    availablePermissions.map(normalizePermission).filter(Boolean)
                ));
                setRole(currentRole);
                setSelectedPermissions((currentRole?.permissions || []).map(
                    getPermissionId
                ).filter(Boolean));
            } catch (requestError) {
                console.error("Error cargando permisos del rol:", requestError);
                setError("No fue posible cargar los permisos del rol.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [roleFromLocation, roleId]);

    const filteredPermissions = useMemo(() => {
        const normalizedSearch = search.toLowerCase().trim();
        if (!normalizedSearch) return permissions;

        return permissions.filter((permission) =>
            `${permission.name} ${permission.code} ${permission.module} ${permission.description || ""}`
                .toLowerCase()
                .includes(normalizedSearch)
        );
    }, [permissions, search]);

    const totalPermissionPages = Math.ceil(filteredPermissions.length / permissionsPerPage);
    const visiblePermissions = useMemo(() => {
        const start = permissionPage * permissionsPerPage;
        return filteredPermissions.slice(start, start + permissionsPerPage);
    }, [filteredPermissions, permissionPage]);

    useEffect(() => {
        setPermissionPage(0);
    }, [search]);

    useEffect(() => {
        if (totalPermissionPages > 0 && permissionPage >= totalPermissionPages) {
            setPermissionPage(totalPermissionPages - 1);
        }
    }, [permissionPage, totalPermissionPages]);

    const togglePermission = (permissionId) => {
        setSaved(false);
        setSelectedPermissions((current) => current.includes(permissionId)
            ? current.filter((id) => id !== permissionId)
            : [...current, permissionId]
        );
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const token = localStorage.getItem("access_token");
            await axios.put(
                `${process.env.REACT_APP_API_URL}/roles/update/${roleId}`,
                { permissions: selectedPermissions },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSaved(true);
        } catch (requestError) {
            console.error("Error asignando permisos al rol:", requestError);
            setError(
                requestError.response?.data?.message ||
                "No fue posible guardar los permisos del rol."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-w-0">
            <TitleCard title="Asignar permisos al rol" />

            <div className="flex items-center justify-between gap-4 my-4">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-700"
                >
                    <ArrowLeft size={18} />
                    Volver a roles
                </button>
                <span className="text-sm text-gray-500">
                    {selectedPermissions.length} permisos seleccionados
                </span>
            </div>

            <form onSubmit={handleSubmit} className="min-w-0 bg-white rounded-lg shadow-md p-5 text-gray-700">
                <div className="flex items-start gap-3 border-b pb-5 mb-5">
                    <KeyRound className="text-blue-600 mt-1" size={22} />
                    <div>
                        <h2 className="text-lg font-semibold">Rol seleccionado</h2>
                        <p className="text-sm text-gray-600">
                            {role?.name || `Rol ${roleId}`}
                            {role?.description ? ` - ${role.description}` : ""}
                        </p>
                    </div>
                </div>

                {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

                <div className="relative mb-4 max-w-md">
                    <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar permisos"
                        className="w-full border border-gray-300 rounded px-10 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                </div>

                {loading ? (
                    <p className="py-8 text-center text-sm text-gray-500">Cargando permisos...</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {visiblePermissions.map((permission) => {
                            const permissionId = permission.id;
                            const isSelected = selectedPermissions.includes(permissionId);
                            return (
                                <div
                                    key={permissionId}
                                    role="checkbox"
                                    aria-checked={isSelected}
                                    tabIndex={0}
                                    onClick={() => togglePermission(permissionId)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            togglePermission(permissionId);
                                        }
                                    }}
                                    className={`flex min-w-0 items-start gap-3 border rounded-lg p-4 cursor-pointer transition select-none ${isSelected
                                            ? "border-blue-600 bg-blue-50"
                                            : "border-gray-200 hover:border-blue-300"
                                        }`}
                                >
                                    <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${isSelected
                                            ? "border-blue-600 bg-blue-600 text-white"
                                            : "border-gray-300"
                                        }`}>
                                        {isSelected && <Check size={14} />}
                                    </span>
                                    <span className="min-w-0 break-words">
                                        <span className="block break-words font-medium">{permission.name}</span>
                                        <span className="block break-words text-sm text-gray-500">
                                            {permission.code || permission.module || "Sin descripción"}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && totalPermissionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <button
                            type="button"
                            disabled={permissionPage === 0}
                            onClick={() => setPermissionPage((page) => page - 1)}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50"
                        >
                            Anterior
                        </button>
                        <span className="text-sm text-gray-500">
                            Página {permissionPage + 1} de {totalPermissionPages}
                        </span>
                        <button
                            type="button"
                            disabled={permissionPage === totalPermissionPages - 1}
                            onClick={() => setPermissionPage((page) => page + 1)}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50"
                        >
                            Siguiente
                        </button>
                    </div>
                )}

                {!loading && filteredPermissions.length === 0 && (
                    <p className="py-8 text-center text-sm text-gray-500">
                        No se encontraron permisos.
                    </p>
                )}

                <div className="flex flex-row flex-nowrap items-center gap-4 mt-6">
                    <GradientButton type="submit" disabled={saving || loading || !role}>
                        {saving ? "Guardando..." : "Guardar"}
                    </GradientButton>
                    <CancelButton onClick={() => navigate(-1)} />
                    {saved && <span className="text-sm text-green-600">Permisos guardados correctamente.</span>}
                </div>
            </form>
        </div>
    );
};

export default RolePermissionAssignment;
